import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, MapPin, Train, Map, MessageCircle, Loader2, ArrowRight } from "lucide-react";

import { BRAND } from "@/mock";

const WEB3FORMS_KEY = "c033885b-af2d-486b-b4ef-776a042737c8";
const EMAILJS_SERVICE  = "service_xl6u5n7";
const EMAILJS_TEMPLATE = "template_zwtzw4s";
const EMAILJS_PUBLIC   = "m6ozIxVD9DcxIIg6C";

const ROOM_TYPES = [
  "Single Room (Non A/C)",
  "Single Room (A/C)",
  "1 BHK Compact (A/C)",
  "1 BHK Large (A/C)",
  "2 BHK (A/C)",
  "3 BHK (A/C)",
];

const TODAY = new Date().toISOString().split("T")[0];

const isValidIndianPhone = (phone) => /^[6-9]\d{9}$/.test(phone);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    room_type: "",
    rental_basis: "Daily",
    check_in: "",
    guests: "1 Guest",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const guestsRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const clearError = (k) => setErrors((prev) => ({ ...prev, [k]: "" }));

  const handleNameChange = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[0-9]/g, "");
    update("name", cleaned);
    if (raw !== cleaned) {
      setErrors((prev) => ({ ...prev, name: "Name cannot contain numbers" }));
    } else {
      clearError("name");
    }
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    update("phone", digits);
    if (digits.length > 0 && digits.length < 10) {
      setErrors((prev) => ({ ...prev, phone: "Phone number must be 10 digits" }));
    } else if (digits.length === 10 && !isValidIndianPhone(digits)) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid Indian mobile number (starts with 6–9)" }));
    } else {
      clearError("phone");
    }
  };

  const handleCheckinChange = (e) => {
    update("check_in", e.target.value);
    clearError("check_in");
    if (e.target.value) {
      setTimeout(() => guestsRef.current?.focus(), 50);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidIndianPhone(form.phone)) {
      newErrors.phone = "Enter a valid 10-digit Indian mobile number (starts with 6–9)";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Enquiry from ${form.name} — Hitha Residency`,
          from_name: form.name,
          name: form.name,
          phone: form.phone,
          email: form.email || "not provided",
          room_type: form.room_type || "not specified",
          rental_basis: form.rental_basis,
          check_in: form.check_in || "not specified",
          guests: form.guests,
          message: form.message || "no message",
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (form.email) {
          try {
            await fetch("https://api.emailjs.com/api/v1.0/email/send", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                service_id: EMAILJS_SERVICE,
                template_id: EMAILJS_TEMPLATE,
                user_id: EMAILJS_PUBLIC,
                template_params: {
                  to_email: form.email,
                  name: form.name,
                  phone: form.phone,
                  room_type: form.room_type || "your preferred room",
                  check_in: form.check_in || "flexible",
                  guests: form.guests,
                  rental_basis: form.rental_basis,
                },
              }),
            });
          } catch (ejErr) {
            console.error("EmailJS error:", ejErr);
          }
        }
        toast.success(
          form.email
            ? `Thank you ${form.name}! Confirmation sent to ${form.email} (check spam if not in inbox). We'll call you at ${form.phone} shortly.`
            : `Thank you ${form.name}! We'll contact you at ${form.phone} shortly.`
        );
        setForm({ name: "", phone: "", email: "", room_type: "", rental_basis: "Daily", check_in: "", guests: "1 Guest", message: "" });
        setErrors({});
      } else {
        throw new Error("failed");
      }
    } catch {
      toast.error("Could not send enquiry. Please call or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 px-5 md:px-10 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-3 mb-3">
            <span className="h-[1px] w-6 bg-[#C8A97E]" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#C8A97E] font-semibold">
              Reach Us
            </span>
            <span className="h-[1px] w-6 bg-[#C8A97E]" />
          </div>
          <h2 className="font-serif text-[40px] sm:text-[46px] font-semibold text-[#1A1A1A] leading-[1.1] mb-3">
            Book Your <em className="text-[#C8A97E] font-normal">Stay</em> Today
          </h2>
          <p className="text-[15px] text-[#666] max-w-xl mx-auto leading-relaxed">
            Fill the form or reach us directly — we'll get back to you right away.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
          {/* Left - contact info card */}
          <div
            className="bg-[#1A1A1A] rounded-[24px] p-8 md:p-10 border border-[#C8A97E]/20 relative overflow-hidden"
            data-testid="contact-info-card"
          >
            <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-[#C8A97E]/6 pointer-events-none" />

            <h3 className="font-serif text-[26px] sm:text-[30px] font-semibold text-white mb-8 leading-tight">
              Get in <em className="text-[#C8A97E] font-normal">Touch</em>
            </h3>

            <InfoItem icon={Phone} label="Mobile">
              <a href={`tel:${BRAND.phone}`} className="text-[#C8A97E] hover:underline">
                {BRAND.phone}
              </a>
            </InfoItem>

            <InfoItem icon={MapPin} label="Address">
              {BRAND.address.line1}
              <br />
              {BRAND.address.line2}
              <br />
              {BRAND.address.line3}
              <br />
              {BRAND.address.line4}
            </InfoItem>

            <InfoItem icon={Train} label="Nearest Metro">
              Chikkabidarakallu Metro Station
              <br />
              <span className="text-[12px] text-white/50">Green Line · ~1 km walk</span>
            </InfoItem>

            <InfoItem icon={Map} label="Google Maps">
              <a
                href={`https://maps.google.com/?q=${BRAND.mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C8A97E] hover:underline inline-flex items-center gap-1"
              >
                View on Google Maps <ArrowRight className="w-3 h-3" />
              </a>
            </InfoItem>

            <a
              href={`https://wa.me/${BRAND.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20b858] text-white rounded-full px-7 py-4 text-[14px] font-semibold mt-6 shadow-[0_8px_22px_rgba(37,211,102,0.3)] transition-all"
              data-testid="contact-whatsapp-big"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp Now
            </a>
          </div>

          {/* Right - form */}
          <form
            onSubmit={handleSubmit}
            className="bg-[#F9F7F3] rounded-[24px] p-8 md:p-10 border border-[#E5E0D8] shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
            data-testid="contact-form"
          >
            <h3 className="font-serif text-[26px] font-semibold text-[#1A1A1A] mb-6">
              Send an Enquiry
            </h3>

            <FormField label="Full Name" error={errors.name}>
              <Input
                placeholder="Your full name"
                value={form.name}
                onChange={handleNameChange}
                className={`bg-white border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20 h-11${errors.name ? " border-red-400" : ""}`}
                data-testid="contact-name"
              />
            </FormField>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Phone Number" error={errors.phone}>
                <Input
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={form.phone}
                  onChange={handlePhoneChange}
                  maxLength={10}
                  inputMode="numeric"
                  className={`bg-white border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20 h-11${errors.phone ? " border-red-400" : ""}`}
                  data-testid="contact-phone"
                />
              </FormField>
              <FormField label="Email (Optional)">
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="bg-white border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20 h-11"
                  data-testid="contact-email"
                />
              </FormField>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Room Type">
                <Select value={form.room_type} onValueChange={(v) => update("room_type", v)}>
                  <SelectTrigger
                    className="bg-white border-[#E5E0D8] h-11"
                    data-testid="contact-room-type"
                  >
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Rental Basis">
                <Select value={form.rental_basis} onValueChange={(v) => update("rental_basis", v)}>
                  <SelectTrigger className="bg-white border-[#E5E0D8] h-11" data-testid="contact-rental">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daily">Daily</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Check-in Date" error={errors.check_in}>
                <Input
                  type="date"
                  value={form.check_in}
                  min={TODAY}
                  onChange={handleCheckinChange}
                  className="bg-white border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20 h-11"
                  data-testid="contact-checkin"
                />
              </FormField>
              <FormField label="Guests">
                <Select value={form.guests} onValueChange={(v) => update("guests", v)}>
                  <SelectTrigger ref={guestsRef} className="bg-white border-[#E5E0D8] h-11" data-testid="contact-guests">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1 Guest">1 Guest</SelectItem>
                    <SelectItem value="2 Guests">2 Guests</SelectItem>
                    <SelectItem value="3 Guests">3 Guests</SelectItem>
                    <SelectItem value="4+ Guests">4+ Guests</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <FormField label="Special Requirements">
              <Textarea
                rows={3}
                placeholder="Any requests or questions..."
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="bg-white border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
                data-testid="contact-message"
              />
            </FormField>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-[#C8A97E] to-[#B0946B] hover:opacity-95 text-[#1A1A1A] rounded-full h-12 text-[14px] font-bold tracking-wide mt-2 shadow-[0_8px_22px_rgba(200,169,126,0.35)]"
              data-testid="contact-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-2">
                  Send Enquiry <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Google Maps embed - one-touch directions */}
        <div className="mt-12" data-testid="contact-map-wrapper">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="inline-flex items-center gap-3 mb-1">
                <span className="h-[1px] w-6 bg-[#C8A97E]" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#C8A97E] font-semibold">
                  One-Touch Directions
                </span>
              </div>
              <h3 className="font-serif text-[22px] sm:text-[26px] font-semibold text-[#1A1A1A]">
                Find us on the map
              </h3>
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${BRAND.mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#2a2a2a] text-[#C8A97E] rounded-full px-6 py-3 text-[13px] font-semibold tracking-wide transition-all"
              data-testid="contact-get-directions"
            >
              <Map className="w-4 h-4" />
              Get Directions
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="rounded-[24px] overflow-hidden border border-[#E5E0D8] shadow-[0_8px_30px_rgba(0,0,0,0.06)] bg-white">
            <iframe
              title="Hitha Residency location"
              src={`https://www.google.com/maps?q=${BRAND.mapsQuery}&output=embed`}
              width="100%"
              height="380"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoItem = ({ icon: Icon, label, children }) => (
  <div className="flex gap-3 mb-6">
    <div className="w-10 h-10 rounded-[10px] bg-[#C8A97E]/10 border border-[#C8A97E]/25 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#C8A97E]" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#C8A97E] font-semibold mb-1">
        {label}
      </div>
      <div className="text-[13px] text-white/80 leading-[1.65]">{children}</div>
    </div>
  </div>
);

const FormField = ({ label, error, children }) => (
  <div className="mb-4">
    <label className="block text-[10px] tracking-[0.18em] uppercase text-[#9A9A9A] font-semibold mb-1.5">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-[11px] text-red-500 font-medium">{error}</p>
    )}
  </div>
);

export default Contact;
